import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useContext } from "react";
import { ConfigurationChangeEvent, } from "@plansoft-configuration-renderer/events";
import { useConfigurationEvent } from "@plansoft-configuration-renderer/framework";
import { useIsNestedLayout } from "@plansoft-configuration-renderer/layout";
import { AutoScrollEnabledContext } from "./AutoScrollEnabledContext";
import { AutoScrollStrategy } from "./AutoScrollStrategy";
import { scrollToNextAttribute } from "./scrollToNextAttribute";
const SCROLL_INTO_VIEW_OPTIONS = {
    behavior: "smooth",
    block: "center",
    inline: "nearest",
};
/**
 * Attempts to auto scroll to position in a configuration
 *
 * Scenario
 * - We scroll to the next attribute
 * - The behavior is triggered by a {@linkcode ConfigurationChangeEvent}
 *
 * 📝 Notes
 * - Attributes need the CSS class `.attribute-wrapper` to identify attributes in the DOM
 * - Each attribute needs a wrapper DOM element
 *   - with an `id` equal to the attribute path in the document, e.g. `id="/A_MODEL"`
 *   - with the css class `.attribute-wrapper` for lookup in the DOM
 * - does not use layout information directly, but the rendered DOM
 *
 * @category Behavior
 *
 * @returns
 */
export function AutoScrollBehavior(props) {
    const { scrollDelay = 100, focusControl = false, scrollIfVisible = false, scrollIntoViewOptions = SCROLL_INTO_VIEW_OPTIONS, strategy = AutoScrollStrategy.NextAttribute, } = props;
    const isNestedLayout = useIsNestedLayout();
    const autoScrollEnabled = useContext(AutoScrollEnabledContext);
    const onConfigurationEvent = useCallback((event) => {
        // disabled by user
        if (!autoScrollEnabled) {
            return;
        }
        // controlled by the root layout, do not render this for nested layouts
        if (isNestedLayout) {
            return;
        }
        if (event instanceof ConfigurationChangeEvent) {
            // use the element from the ref
            const activeElement = document.activeElement;
            setTimeout(() => {
                scrollToNextAttribute(activeElement, {
                    focusControl,
                    scrollIfVisible,
                    scrollIntoViewOptions,
                    strategy,
                });
            }, scrollDelay);
        }
    }, [
        scrollDelay,
        focusControl,
        scrollIfVisible,
        scrollIntoViewOptions,
        strategy,
        isNestedLayout,
        autoScrollEnabled,
    ]);
    useConfigurationEvent({
        onConfigurationEvent: onConfigurationEvent,
    });
    return _jsx(Fragment, {});
}
//# sourceMappingURL=AutoScrollBehavior.js.map